import React, { useState } from 'react';

/**
 * Gestionnaire de colonnes personnalisables
 */
const ColumnManager = ({ columns, visibleColumns, onColumnsChange, onClose }) => {
  const [localVisibleColumns, setLocalVisibleColumns] = useState(visibleColumns);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState(null);

  const handleToggleColumn = (columnId) => {
    setLocalVisibleColumns(prev => {
      if (prev.includes(columnId)) {
        return prev.filter(id => id !== columnId);
      } else {
        return [...prev, columnId];
      }
    });
  };

  const handleSelectAll = () => {
    setLocalVisibleColumns(columns.map(col => col.id));
  };

  const handleDeselectAll = () => {
    // Garder au moins 3 colonnes essentielles
    setLocalVisibleColumns(['checkbox', 'reference', 'actions']);
  };

  const handleApply = () => {
    onColumnsChange(localVisibleColumns);
    onClose();
  };

  const handleReset = () => {
    // Colonnes par défaut
    const defaultColumns = ['checkbox', 'reference', 'type', 'client', 'opportunite', 
                           'operation', 'date_creation', 'total', 'statut_doc', 
                           'statut_paiement', 'reste', 'actions'];
    setLocalVisibleColumns(defaultColumns);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
            </svg>
            <h3 className="text-lg font-semibold">Gérer les colonnes</h3>
          </div>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-emerald-500 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Info bar */}
        <div className="bg-blue-50 border-b border-blue-100 px-6 py-3">
          <p className="text-sm text-blue-700">
            💡 Cochez les colonnes à afficher. Les colonnes essentielles (☑️, Ref, Actions) restent toujours visibles.
          </p>
        </div>

        {/* Buttons */}
        <div className="px-6 py-4 border-b border-gray-200 flex gap-2">
          <button
            onClick={handleSelectAll}
            className="px-3 py-1.5 text-sm bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200 transition-colors"
          >
            Tout sélectionner
          </button>
          <button
            onClick={handleDeselectAll}
            className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            Tout désélectionner
          </button>
          <button
            onClick={handleReset}
            className="px-3 py-1.5 text-sm bg-orange-100 text-orange-700 rounded hover:bg-orange-200 transition-colors"
          >
            Réinitialiser
          </button>
          <div className="ml-auto text-sm text-gray-500 flex items-center">
            {localVisibleColumns.length} / {columns.length} colonnes
          </div>
        </div>

        {/* Columns list */}
        <div className="px-6 py-4 max-h-96 overflow-y-auto">
          <div className="space-y-2">
            {columns.map((column) => {
              const isVisible = localVisibleColumns.includes(column.id);
              const isEssential = ['checkbox', 'reference', 'actions'].includes(column.id);
              
              return (
                <div
                  key={column.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                    isVisible 
                      ? 'bg-emerald-50 border-emerald-200' 
                      : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                  } ${isEssential ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  onClick={() => !isEssential && handleToggleColumn(column.id)}
                >
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={isVisible}
                    onChange={() => !isEssential && handleToggleColumn(column.id)}
                    disabled={isEssential}
                    className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                  />
                  
                  {/* Column info */}
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 flex items-center gap-2">
                      {column.label}
                      {isEssential && (
                        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                          Essentielle
                        </span>
                      )}
                    </div>
                    {column.description && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {column.description}
                      </div>
                    )}
                  </div>
                  
                  {/* Status indicator */}
                  <div className={`w-2 h-2 rounded-full ${isVisible ? 'bg-emerald-500' : 'bg-gray-300'}`}></div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleApply}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
          >
            Appliquer
          </button>
        </div>
      </div>
    </div>
  );
};

export default ColumnManager;
